import React, { useEffect, useState, useCallback } from "react";
import { Modal, Button, Form, Row, Col, Alert, Spinner } from "react-bootstrap";
import { Type, AlignLeft, Clock, Users, Repeat } from "lucide-react";
import CalendarRecurrenceForm from "./CalendarRecurrenceForm";
import { toLocalISO } from "./CalendarUtils";
import { useWorkspaces } from "../../hooks/useWorkspaces";
import { apiClient } from "../../services/apiClient";

const DEFAULT_RECURRENCE_RULE = {
  recurrenceType: "none",
  recurrenceInterval: 1,
  dayOfWeekMask: 0,
  dayOfMonth: 0,
  monthOfYear: 0,
  weekOfMonth: 0,
  endType: "NEVER",
  endCount: 0,
  endDate: "",
};

const DURATION_PRESETS = [15, 30, 45, 60, 90, 120];

const SectionLabel = ({ icon: Icon, children }) => (
  <div className="d-flex align-items-center gap-1 mb-1">
    <Icon size={13} className="text-secondary flex-shrink-0" />
    <span className="small fw-semibold text-secondary">{children}</span>
  </div>
);

const CalendarEventDialog = ({
  token,
  show,
  onClose,
  editingEvent,
  initialTimes,
  workspaceId,
  addEvent,
  editEvent,
  removeEvent,
  modifyInstance,
  cancelInstance,
  loadEvents,
  currentRange,
}) => {
  const { getMembers } = useWorkspaces({ token });
  const [members, setMembers] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [editScope, setEditScope] = useState("none");
  const [form, setForm] = useState({
    title: "",
    description: "",
    startTime: "",
    endTime: "",
  });
  const [isAllDay, setIsAllDay] = useState(false);
  const [allDayStart, setAllDayStart] = useState("");
  const [allDayEnd, setAllDayEnd] = useState("");
  const [recurrenceRule, setRecurrenceRule] = useState(DEFAULT_RECURRENCE_RULE);
  const [showRecurrenceForm, setShowRecurrenceForm] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [titleError, setTitleError] = useState(false);
  const [saving, setSaving] = useState(false);

  const isRecurringInstance = !!(editingEvent?.parentId && editingEvent?.recurrenceRule);

  // Fetch members when dialog opens
  useEffect(() => {
    if (show && workspaceId) {
      const fetchMembers = async () => {
        try {
          const membersList = await getMembers(workspaceId, token);
          setMembers(membersList || []);
        } catch (err) {
          console.error("Failed to fetch members:", err);
          setMembers([]);
        }
      };
      fetchMembers();
    }
  }, [show, workspaceId, getMembers]);

  // Initialize form on open
  useEffect(() => {
    if (!show) return;
    setSaveError(null);
    setTitleError(false);
    setIsAllDay(false);

    if (editingEvent) {
      const startISO = toLocalISO(editingEvent.startTime || editingEvent.start);
      const endISO = toLocalISO(editingEvent.endTime || editingEvent.end);

      setForm({
        title: editingEvent.title || "",
        description: editingEvent.description || "",
        startTime: startISO,
        endTime: endISO,
      });

      const startDate = startISO ? startISO.split("T")[0] : "";
      const endDate = endISO ? endISO.split("T")[0] : "";
      setAllDayStart(startDate);
      setAllDayEnd(endDate);

      setEditScope("none");
      setSelectedMembers(
        (editingEvent.linkedProfiles || []).map(lp => lp.profileId)
      );

      if (editingEvent.recurrenceRule && editingEvent.recurrenceRule.recurrenceType) {
        setRecurrenceRule({
          ...DEFAULT_RECURRENCE_RULE,
          ...editingEvent.recurrenceRule,
          endDate: editingEvent.recurrenceRule.endDate
            ? editingEvent.recurrenceRule.endDate.split("T")[0]
            : "",
        });
        setEditScope(isRecurringInstance ? "instance" : "series");
        setShowRecurrenceForm(true);
      } else {
        setRecurrenceRule(DEFAULT_RECURRENCE_RULE);
        setShowRecurrenceForm(false);
      }
    } else if (initialTimes) {
      const startISO = toLocalISO(initialTimes.start);
      const endISO = toLocalISO(initialTimes.end);

      setForm({
        title: "",
        description: "",
        startTime: startISO,
        endTime: endISO,
      });

      const startDate = startISO ? startISO.split("T")[0] : "";
      const endDate = endISO ? endISO.split("T")[0] : "";
      setAllDayStart(startDate);
      setAllDayEnd(endDate);

      setRecurrenceRule(DEFAULT_RECURRENCE_RULE);
      setSelectedMembers([]);
      // New events: no recurrence form shown by default, editScope stays "none"
      setEditScope("none");
      setShowRecurrenceForm(false);
    }
  }, [show, editingEvent, initialTimes]);

  // Handle series vs instance toggle — update times when switching scope
  useEffect(() => {
    if (editingEvent && isRecurringInstance) {
      if (editScope === "series" && editingEvent.parentStartTime) {
        setForm(prev => ({
          ...prev,
          startTime: toLocalISO(editingEvent.parentStartTime),
          endTime: toLocalISO(editingEvent.parentEndTime),
        }));
      } else {
        setForm(prev => ({
          ...prev,
          startTime: toLocalISO(editingEvent.startTime),
          endTime: toLocalISO(editingEvent.endTime),
        }));
      }
    }
  }, [editScope, editingEvent, isRecurringInstance]);

  // Compute current duration in minutes for highlighting duration buttons
  const currentDurationMins = (() => {
    if (isAllDay) return null;
    const start = new Date(form.startTime);
    const end = new Date(form.endTime);
    if (isNaN(start) || isNaN(end)) return null;
    const diff = (end - start) / 60000;
    return diff > 0 ? diff : null;
  })();

  // Build effective start/end for the payload, respecting all-day mode
  const buildEffectiveTimes = () => {
    if (isAllDay) {
      const start = allDayStart ? `${allDayStart}T00:00:00` : null;
      const end = allDayEnd ? `${allDayEnd}T23:59:59` : null;
      return { startTime: start ? new Date(start).toISOString() : null, endTime: end ? new Date(end).toISOString() : null };
    }
    return {
      startTime: form.startTime ? new Date(form.startTime).toISOString() : null,
      endTime: form.endTime ? new Date(form.endTime).toISOString() : null,
    };
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      setTitleError(true);
      return;
    }
    setTitleError(false);

    const { startTime, endTime } = buildEffectiveTimes();
    if (!startTime || !endTime) return;

    const linkedProfiles =
      selectedMembers.length > 0
        ? selectedMembers.map(Number)
        : undefined;

    const apiPayload = {
      title: form.title,
      description: form.description,
      startTime,
      endTime,
      workspaceId,
      linkedProfiles,
    };

    // Include recurrence rule whenever it is set (non-none) regardless of editScope
    if (recurrenceRule.recurrenceType !== "none") {
      apiPayload.recurrenceRule = recurrenceRule;
    }

    try {
      setSaving(true);
      if (!editingEvent) {
        await addEvent(apiPayload);
      } else if (editScope === "series" || editScope === "none") {
        await editEvent(editingEvent.parentId || editingEvent.id, apiPayload);
      } else {
        await modifyInstance({
          ...apiPayload,
          parentId: editingEvent.parentId,
          originalStartTime: editingEvent.originalStartTime || editingEvent.startTime,
        }, currentRange.start, currentRange.end);
      }
      await loadEvents(currentRange.start, currentRange.end);
      onClose();
    } catch (err) {
      console.error("Save failed:", err);
      setSaveError(err?.message || "Speichern fehlgeschlagen. Bitte erneut versuchen.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      if (editScope === "series") {
        await removeEvent(editingEvent.parentId || editingEvent.id, currentRange.start, currentRange.end);
      } else {
        await cancelInstance({
          workspaceId,
          parentId: editingEvent.parentId,
          originalStartTime: editingEvent.originalStartTime || editingEvent.startTime,
        }, currentRange.start, currentRange.end);
      }
      await loadEvents(currentRange.start, currentRange.end);
      onClose();
    } catch (err) {
      console.error("Delete failed:", err);
    }
  };

  const MemberChip = ({ member, selected, onToggle }) => {
    const color = member.color || "#6c757d";

    return (
      <Button
        size="sm"
        onClick={() => onToggle(member.id)}
        className="me-2 mb-2 d-inline-flex align-items-center"
        style={{
          backgroundColor: selected ? "#e7f1ff" : "#fff",
          borderColor: selected ? "#b6d4fe" : "#dee2e6",
          color: "#212529",
          boxShadow: "none",
        }}
        variant="outline-light"
      >
        <span
          className="rounded-circle me-2"
          style={{
            width: 8,
            height: 8,
            backgroundColor: color,
            flexShrink: 0,
          }}
        />
        <span className="small fw-medium">{member.displayName}</span>
      </Button>
    );
  };

  const toggleMember = useCallback((memberId) => {
    setSelectedMembers((prev) =>
      prev.includes(memberId)
        ? prev.filter((id) => id !== memberId)
        : [...prev, memberId]
    );
  }, []);

  const handleToggleRecurrence = () => {
    if (showRecurrenceForm) {
      // Collapsing — reset recurrence to none
      setShowRecurrenceForm(false);
      setRecurrenceRule(DEFAULT_RECURRENCE_RULE);
    } else {
      setShowRecurrenceForm(true);
      // If editing a series and recurrence was already set, keep it; otherwise show default form
    }
  };

  return (
    <Modal show={show} onHide={onClose} centered size="lg" backdrop="static">
      <Modal.Header closeButton className="bg-light border-bottom-0 pb-2">
        <Modal.Title className="fs-6 fw-bold">
          {editingEvent ? "Termin bearbeiten" : "Neuen Termin erstellen"}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="pt-2">
        {saveError && (
          <Alert variant="danger" className="py-2 small mb-3" onClose={() => setSaveError(null)} dismissible>
            {saveError}
          </Alert>
        )}

        {isRecurringInstance && editScope === "instance" && (
          <Alert variant="info" className="d-flex justify-content-between align-items-center py-2 mb-3">
            <span className="small">Sie bearbeiten eine Einzelinstanz dieser Serie.</span>
            <Button size="sm" variant="outline-primary" onClick={() => setEditScope("series")}>
              Ganze Serie bearbeiten
            </Button>
          </Alert>
        )}

        <Form noValidate>
          {/* Title */}
          <Form.Group className="mb-3">
            <SectionLabel icon={Type}>Titel</SectionLabel>
            <Form.Control
              placeholder="z.B. Team Meeting"
              value={form.title}
              isInvalid={titleError}
              onChange={(e) => {
                setForm({ ...form, title: e.target.value });
                if (titleError && e.target.value.trim()) setTitleError(false);
              }}
            />
            <Form.Control.Feedback type="invalid">
              Bitte einen Titel eingeben.
            </Form.Control.Feedback>
          </Form.Group>

          {/* Description */}
          <Form.Group className="mb-3">
            <SectionLabel icon={AlignLeft}>Beschreibung</SectionLabel>
            <Form.Control
              as="textarea"
              rows={2}
              placeholder="Optionale Beschreibung..."
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
          </Form.Group>

          <hr className="my-3" />

          {/* Time Section */}
          <div className="mb-3">
            <div className="d-flex align-items-center justify-content-between mb-2">
              <SectionLabel icon={Clock}>Zeit</SectionLabel>
              <Form.Check
                type="switch"
                id="all-day-switch"
                label={<span className="small text-secondary">Ganztägig</span>}
                checked={isAllDay}
                onChange={(e) => setIsAllDay(e.target.checked)}
                className="mb-0"
              />
            </div>

            {isAllDay ? (
              <Row className="g-2">
                <Col xs={6}>
                  <Form.Label className="small text-muted mb-1">Von</Form.Label>
                  <Form.Control
                    type="date"
                    value={allDayStart}
                    onChange={(e) => {
                      setAllDayStart(e.target.value);
                      // Auto-extend end date if it's before start
                      if (allDayEnd && e.target.value > allDayEnd) {
                        setAllDayEnd(e.target.value);
                      }
                    }}
                  />
                </Col>
                <Col xs={6}>
                  <Form.Label className="small text-muted mb-1">Bis</Form.Label>
                  <Form.Control
                    type="date"
                    value={allDayEnd}
                    min={allDayStart}
                    onChange={(e) => setAllDayEnd(e.target.value)}
                  />
                </Col>
              </Row>
            ) : (
              <>
                <Row className="g-2 mb-2">
                  <Col xs={6}>
                    <Form.Label className="small text-muted mb-1">Start</Form.Label>
                    <Form.Control
                      type="datetime-local"
                      value={form.startTime}
                      onChange={(e) => {
                        const newStartStr = e.target.value;
                        if (!newStartStr) {
                          setForm({ ...form, startTime: newStartStr });
                          return;
                        }
                        const oldStart = new Date(form.startTime);
                        const oldEnd = new Date(form.endTime);
                        let durationMs = 60 * 60 * 1000;
                        if (!isNaN(oldStart) && !isNaN(oldEnd)) {
                          durationMs = oldEnd - oldStart;
                        }
                        if (durationMs < 0) durationMs = 60 * 60 * 1000;

                        const newStart = new Date(newStartStr);
                        if (!isNaN(newStart)) {
                          const newEnd = new Date(newStart.getTime() + durationMs);
                          setForm({
                            ...form,
                            startTime: newStartStr,
                            endTime: toLocalISO(newEnd),
                          });
                        } else {
                          setForm({ ...form, startTime: newStartStr });
                        }
                      }}
                    />
                  </Col>
                  <Col xs={6}>
                    <Form.Label className="small text-muted mb-1">Ende</Form.Label>
                    <Form.Control
                      type="datetime-local"
                      value={form.endTime}
                      min={form.startTime}
                      onChange={(e) => setForm({ ...form, endTime: e.target.value })}
                    />
                  </Col>
                </Row>

                {/* Duration presets */}
                <div className="d-flex flex-wrap gap-1">
                  {DURATION_PRESETS.map(mins => {
                    const isActive = currentDurationMins !== null && Math.round(currentDurationMins) === mins;
                    return (
                      <Button
                        key={mins}
                        variant={isActive ? "primary" : "outline-secondary"}
                        size="sm"
                        style={{ minWidth: 40 }}
                        onClick={() => {
                          const start = new Date(form.startTime);
                          if (!isNaN(start)) {
                            const end = new Date(start.getTime() + mins * 60000);
                            setForm({ ...form, endTime: toLocalISO(end) });
                          }
                        }}
                      >
                        {mins < 60 ? `${mins}m` : `${mins / 60}h`}
                      </Button>
                    );
                  })}
                </div>
              </>
            )}
          </div>

          <hr className="my-3" />

          {/* Members */}
          {members.length > 0 && (
            <>
              <Form.Group className="mb-3">
                <SectionLabel icon={Users}>Teilnehmer</SectionLabel>
                <div className="d-flex flex-wrap">
                  {members.map((member) => (
                    <MemberChip
                      key={member.id}
                      member={member}
                      selected={selectedMembers.includes(member.id)}
                      onToggle={toggleMember}
                    />
                  ))}
                </div>
              </Form.Group>
              <hr className="my-3" />
            </>
          )}

          {/* Recurrence */}
          <div className="mb-2">
            <div className="d-flex align-items-center justify-content-between">
              <SectionLabel icon={Repeat}>Wiederholung</SectionLabel>
              {!showRecurrenceForm ? (
                <Button
                  variant="link"
                  size="sm"
                  className="p-0 text-primary small"
                  onClick={handleToggleRecurrence}
                >
                  Wiederholung hinzufügen
                </Button>
              ) : (
                <Button
                  variant="link"
                  size="sm"
                  className="p-0 text-danger small"
                  onClick={handleToggleRecurrence}
                >
                  Entfernen
                </Button>
              )}
            </div>

            {showRecurrenceForm && (
              <div className="mt-2 p-3 rounded" style={{ backgroundColor: "#f8f9fa" }}>
                {isRecurringInstance && (
                  <div className="mb-2">
                    <Form.Check
                      type="switch"
                      id="edit-series-switch"
                      label={<span className="small">Ganze Serie bearbeiten</span>}
                      checked={editScope === "series"}
                      onChange={(e) => setEditScope(e.target.checked ? "series" : "instance")}
                    />
                  </div>
                )}
                <CalendarRecurrenceForm
                  recurrenceRule={recurrenceRule}
                  setRecurrenceRule={setRecurrenceRule}
                />
              </div>
            )}
          </div>
        </Form>
      </Modal.Body>

      <Modal.Footer className="bg-light border-top d-flex align-items-center">
        {editingEvent && (
          <Button
            variant="link"
            className="text-danger me-auto small p-0"
            onClick={handleDelete}
            disabled={saving}
          >
            Termin löschen
          </Button>
        )}
        <Button variant="secondary" size="sm" onClick={onClose} disabled={saving}>
          Abbrechen
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={handleSave}
          disabled={saving}
          className="px-4"
        >
          {saving ? (
            <>
              <Spinner animation="border" size="sm" className="me-1" />
              Speichern...
            </>
          ) : (
            editingEvent ? "Speichern" : "Hinzufügen"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CalendarEventDialog;
